import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { configureAmplify } from '@/lib/amplify-config'
import { fetchAuthSession } from 'aws-amplify/auth'

// Import SEO component
import { SEO } from '@/components/SEO'

// Import page components
import { LandingPage } from '@/pages/Landing'
import { LoginPage } from '@/pages/Login'
import { RegisterPage } from '@/pages/Register'
import { DashboardPage } from '@/pages/Dashboard'
import { FormsListPage } from '@/pages/FormsList'
import { FormDetailPage } from '@/pages/FormDetail'
import { FormMessagesPage } from '@/pages/FormMessages'
import { SettingsPage } from '@/pages/Settings'
import { PrivacyPage } from '@/pages/Privacy'
import { TermsPage } from '@/pages/Terms'
import { SetupPage } from '@/pages/Setup'

// Configure Amplify on app load
configureAmplify()

// Strip trailing slashes from URLs (except root path)
function StripTrailingSlash({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    return <Navigate to={location.pathname.slice(0, -1)} replace />;
  }

  return <>{children}</>;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      await fetchAuthSession()
      setIsAuthenticated(true)
    } catch {
      setIsAuthenticated(false)
    }
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <StripTrailingSlash>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={
            <>
              <SEO
                title="FormTo - Turn web forms into Telegram messages"
                description="Get instant Telegram notifications when someone submits your form. No backend required."
              />
              <LandingPage />
            </>
          } />
          <Route path="/login" element={
            <>
              <SEO
                title="Login - FormTo"
                description="Sign in to your FormTo account to manage forms and notifications."
              />
              <LoginPage />
            </>
          } />
          <Route path="/register" element={
            <>
              <SEO
                title="Sign Up - FormTo"
                description="Create a free FormTo account to start receiving form submissions in Telegram."
              />
              <RegisterPage />
            </>
          } />
          <Route path="/privacy" element={
            <>
              <SEO
                title="Privacy Policy - FormTo"
                description="Learn how FormTo handles your data and protects your privacy."
              />
              <PrivacyPage />
            </>
          } />
          <Route path="/terms" element={
            <>
              <SEO
                title="Terms of Service - FormTo"
                description="Terms and conditions for using FormTo services."
              />
              <TermsPage />
            </>
          } />

          {/* Protected routes - noindex */}
          <Route
            path="/app"
            element={
              <>
                <SEO
                  title="Dashboard - FormTo"
                  description="Manage your forms and Telegram notifications"
                  noindex={true}
                />
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              </>
            }
          />
          <Route
            path="/app/setup"
            element={
              <>
                <SEO
                  title="Setup - FormTo"
                  description="Set up your FormTo account"
                  noindex={true}
                />
                <ProtectedRoute>
                  <SetupPage />
                </ProtectedRoute>
              </>
            }
          />
          <Route
            path="/app/forms"
            element={
              <>
                <SEO
                  title="Forms - FormTo"
                  description="Manage your forms"
                  noindex={true}
                />
                <ProtectedRoute>
                  <FormsListPage />
                </ProtectedRoute>
              </>
            }
          />
          <Route
            path="/app/forms/:id"
            element={
              <>
                <SEO
                  title="Form Details - FormTo"
                  description="View and manage form details"
                  noindex={true}
                />
                <ProtectedRoute>
                  <FormDetailPage />
                </ProtectedRoute>
              </>
            }
          />
          <Route
            path="/app/forms/:id/messages"
            element={
              <>
                <SEO
                  title="Form Messages - FormTo"
                  description="View form submissions"
                  noindex={true}
                />
                <ProtectedRoute>
                  <FormMessagesPage />
                </ProtectedRoute>
              </>
            }
          />
          <Route
            path="/app/settings"
            element={
              <>
                <SEO
                  title="Settings - FormTo"
                  description="Manage account settings"
                  noindex={true}
                />
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              </>
            }
          />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </StripTrailingSlash>
      <Toaster />
    </BrowserRouter>
  )
}

export default App
